/**
 * Debug page to test authentication
 * DEV ONLY - Remove before production deployment
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth-service';

export default function DebugAuthPage() {
  const [status, setStatus] = useState<string>('Checking permissions...');
  interface TokenInfo {
    idToken?: string;
    accessToken?: string;
    refreshToken?: string;
  }
  const [tokens, setTokens] = useState<TokenInfo | null>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const navigate = useNavigate();

  // Only allow in dev environment and for admin users
  useEffect(() => {
    checkAuthorization();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- checkAuthorization is defined below, run once on mount
  }, []);

  const checkAuthorization = async () => {
    const user = await authService.getCurrentUser();
    
    // Check if admin (in production, also check environment)
    const isAdmin = user?.groups?.includes('Administrators');
    const isDev = window.location.hostname.includes('dev.') || window.location.hostname.includes('localhost');
    
    if (!isAdmin || !isDev) {
      setStatus('Access Denied: Admin access required in dev environment only');
      setTimeout(() => navigate('/'), 2000);
      return;
    }
    
    setIsAuthorized(true);
    setStatus('Ready to test');
  };

  if (!isAuthorized) {
    return (
      <div className="px-8 pt-4 pb-8 text-center">
        <h2>⚠️ Access Denied</h2>
        <p>{status}</p>
        <p className="text-sm text-gray-600 mt-4">
          This debug page is only available to administrators in development environment.
        </p>
      </div>
    );
  }

  const testAuth = async () => {
    setStatus('Testing...');
    
    try {
      // Test 1: Get current user
      const user = await authService.getCurrentUser();
      if (!user) {
        setStatus('FAIL: No current user');
        return;
      }
      setStatus(`User found: ${user.email}`);
      
      // Test 2: Get tokens
      const fetchedTokens = await authService.getTokens();
      if (!fetchedTokens) {
        setStatus('FAIL: No tokens retrieved');
        return;
      }
      
      setTokens(fetchedTokens);
      setStatus(`SUCCESS: Tokens retrieved!`);
      
      // Test 3: Make API call with ID Token (API Gateway authorizer)
      const response = await fetch('https://wjcn4ucd1d.execute-api.us-east-1.amazonaws.com/dev/projects', {
        headers: {
          'Authorization': `Bearer ${fetchedTokens.idToken}`,
        },
      });
      
      const data = await response.json();
      setStatus(`API Response: ${response.status} - ${JSON.stringify(data).substring(0, 200)}`);
      
    } catch (error: unknown) {
      setStatus(`ERROR: ${error.message}`);
    }
  };

  const checkLocalStorage = () => {
    const keys = Object.keys(localStorage).filter(k => k.includes('Cognito'));
    setStatus(`Found ${keys.length} Cognito keys in localStorage:\n` + keys.join('\n'));
  };

  return (
    <div className="px-8 pt-4 pb-8 max-w-3xl mx-auto">
      <h1>Authentication Debug Page</h1>
      
      <div className="mt-8 flex gap-4">
        <button
          onClick={testAuth}
          className="py-4 px-8 bg-blue-700 text-white border-none rounded cursor-pointer text-base"
        >
          Test Authentication Flow
        </button>
        
        <button
          onClick={checkLocalStorage}
          className="py-4 px-8 bg-success text-success-foreground border-none rounded cursor-pointer text-base"
        >
          Check LocalStorage
        </button>
      </div>

      <div className="mt-8 p-4 bg-gray-100 dark:bg-slate-800 rounded whitespace-pre-wrap font-mono">
        <strong>Status:</strong><br/>
        {status}
      </div>

      {tokens && (
        <div className="mt-4 p-4 bg-info/10 dark:bg-info/25 rounded">
          <strong>Tokens Found:</strong><br/>
          <div className="font-mono text-sm mt-2">
            ID Token: {tokens.idToken.substring(0, 50)}...<br/>
            Access Token: {tokens.accessToken.substring(0, 50)}...<br/>
            Access Token Length: {tokens.accessToken.length}<br/>
          </div>
        </div>
      )}

      <div className="mt-8 p-4 bg-amber-100 rounded">
        <h3>Instructions:</h3>
        <ol>
          <li>Click "Test Authentication Flow"</li>
          <li>Watch the status update</li>
          <li>If it works, tokens are fine and axios is the problem</li>
          <li>If it fails, we know where the issue is</li>
        </ol>
      </div>
    </div>
  );
}

